// src/pages/guest/GuestHome.jsx
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { shop as shopApi } from '../../api';
import { useAuth } from '../../hooks/useAuth';
import Icon from '../../components/Icon';
import { COUNTRIES_SORTED, getCountry } from '../../data/countries';

export default function GuestHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { exitGuest } = useAuth();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState('KG');
  const [search, setSearch] = useState('');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [country]);

  const load = async () => {
    setLoading(true);
    try {
      const list = await shopApi.publicList({ country });
      setShops(list);
    } catch (e) { setShops([]); }
    setLoading(false);
  };

  const filtered = search.trim()
    ? shops.filter(s =>
        s.name.toLowerCase().includes(search.toLowerCase()) ||
        s.id.toLowerCase().includes(search.toLowerCase()) ||
        (s.description || '').toLowerCase().includes(search.toLowerCase())
      )
    : shops;

  const currentCountry = getCountry(country);

  return (
    <div className="page" style={{ paddingBottom: 24 }}>
      <div className="page-header">
        <div style={{ flex: 1 }}>
          <p className="page-subtitle">{t("guest.homeTitle").toUpperCase()}</p>
          <h1 className="page-title">{t("guest.homeSubtitle")}</h1>
        </div>
        <button
          onClick={() => { exitGuest(); navigate('/'); }}
          style={{
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)', padding: '8px 12px',
            color: 'var(--fg-muted)', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'var(--font-display)',
          }}
        >
          {t('auth.signIn')}
        </button>
      </div>

      <div style={{ padding: '0 20px', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          height: 48, padding: '0 16px',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
        }}>
          <Icon name="search" size={18} color="var(--fg-faint)" />
          <input
            placeholder={t("customerCatalog.search")}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              all: 'unset', flex: 1, fontSize: 15,
              fontFamily: 'inherit', color: 'var(--fg)',
            }}
          />
        </div>
        <select className="select" value={country} onChange={e => setCountry(e.target.value)}>
          {COUNTRIES_SORTED.map(c => (
            <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="icon"><Icon name="store" size={28} stroke={1.5} /></div>
          <p>{search ? t('products.noProductsFound') : t('guest.noShops')}</p>
        </div>
      ) : (
        <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(s => (
            <ShopCard key={s.id} shop={s} onClick={() => navigate(`/shop/${s.id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}

function ShopCard({ shop, onClick }) {
  const { t } = useTranslation();
  return (
    <div className="card fade-up" onClick={onClick} style={{ cursor: 'pointer' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        {shop.photo ? (
          <img src={shop.photo} alt={shop.name}
            style={{ width: 56, height: 56, borderRadius: 'var(--r-sm)', objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <div style={{
            width: 56, height: 56, borderRadius: 'var(--r-sm)',
            background: 'linear-gradient(135deg, var(--brand-primary-soft), var(--brand-accent-soft))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--brand-primary)', flexShrink: 0,
          }}>
            <Icon name="store" size={24} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {shop.name}
          </p>
          {shop.description && (
            <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {shop.description}
            </p>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            {shop.deliveryEnabled && (
              <span className="badge badge-accent">
                <Icon name="truck" size={11} stroke={2} style={{ marginRight: 3 }} />
                {t('settings.delivery')}
              </span>
            )}
            {shop.cartEnabled && (
              <span className="badge badge-primary">
                <Icon name="cart" size={11} stroke={2} style={{ marginRight: 3 }} />
                {t('cart.title')}
              </span>
            )}
            <span className="badge" style={{ fontFamily: 'var(--font-mono)' }}>ID {shop.id.slice(0, 8)}</span>
          </div>
        </div>
        <Icon name="chevronRight" size={16} color="var(--fg-faint)" stroke={2.5} />
      </div>
    </div>
  );
}
